import { queryClient, trpc, trpcClient } from '@/lib/trpc'
import { toast } from 'sonner'
import type { IntegrationDTO } from '@deepguide-ai/types'
import { GithubIcon } from 'lucide-react'
import { IntegrationProvider } from '../types'
import { createOAuthConnectLifecycle } from '../oauth-connect-lifecycle'

const GitHubIconWrapper = () => <GithubIcon className='h-5 w-5' />

/** Open synchronously, then mint a fresh, single-use authorization request for this attempt. */
export function openGitHubInstallPopup(workspaceId?: string): Window | null {
  if (!workspaceId) {
    toast.error('Select a workspace before connecting GitHub');
    return null;
  }
  const width = 800;
  const height = 600;
  const popup = window.open(
    'about:blank',
    'github-app-install',
    `width=${width},height=${height},left=${window.screenX + (window.outerWidth - width) / 2},top=${window.screenY + (window.outerHeight - height) / 2}`,
  );
  if (!popup) return null;
  // An imperative request is needed after opening the popup; cached query data would reuse a consumed state.
  void trpcClient.githubApp.getInstallationUrl.query({ workspaceId }).then((url: string) => {
    if (!popup.closed) popup.location.href = url;
  }).catch((error: unknown) => {
    popup.close();
    toast.error('Could not start GitHub authorization', { description: error instanceof Error ? error.message : 'Try again.' });
  });
  return popup;
}

export const GitHubProvider: IntegrationProvider = {
  id: 'GitHub',
  type: 'github',
  name: 'GitHub',
  description: 'Connect GitHub repositories, pull requests and issues',

  Icon: GitHubIconWrapper,

  postAuthAction: 'SELECT_REPOSITORY',

  handleConnect: async ({ workspaceId, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish);
    try {
      const isConfigured = await trpcClient.githubApp.isConfigured.query();

      if (!isConfigured) {
        toast.error('GitHub App integration is not configured. Please contact your administrator.');
        finish('error');
        return;
      }

      const existingRepos = await trpcClient.githubApp.listConnectedRepositories.query({ workspaceId });

      if (existingRepos && existingRepos.length > 0) {
        onSuccess?.({ success: true, alreadyConnected: true });
        return;
      }

      const authWindow = openGitHubInstallPopup(workspaceId);
      if (!authWindow) {
        toast.error('Allow pop-ups to connect GitHub');
        finish('error');
        return;
      }

      const messageListener = async (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === 'github-app-installed' && event.data?.success) {
          await queryClient.invalidateQueries({
            queryKey: trpc.githubApp.listInstallations.queryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: trpc.githubApp.listConnectedRepositories.queryKey(),
          });
          await queryClient.invalidateQueries({
            queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
          });

          setTimeout(async () => {
            await queryClient.refetchQueries({
              queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
            });
            await queryClient.refetchQueries({
              queryKey: trpc.githubApp.listConnectedRepositories.queryKey({ workspaceId }),
            });
            await queryClient.refetchQueries({
              queryKey: trpc.githubApp.listInstallations.queryKey(),
            });

            onSuccess?.({ success: true });
          }, 500);

          window.removeEventListener('message', messageListener);
          console.log('GitHub App installation success');

          authWindow?.close();
          finish('success');
        }

        if (event.data?.type === 'github-app-error') {
          console.error('GitHub App installation error:', event.data.error);
          window.removeEventListener('message', messageListener);
          authWindow?.close();
          toast.error(`GitHub App installation failed: ${event.data.error || 'Unknown error'}`);
          finish('error');
        }
      };

      window.addEventListener('message', messageListener);

      const checkInstallation = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkInstallation);
            window.removeEventListener('message', messageListener);

            queryClient.invalidateQueries({
              queryKey: trpc.githubApp.listInstallations.queryKey(),
            });
            queryClient.invalidateQueries({
              queryKey: trpc.githubApp.listConnectedRepositories.queryKey(),
            });
            finish('closed');
          }
        } catch (_e) {
          clearInterval(checkInstallation);
          window.removeEventListener('message', messageListener);
          finish('closed');
        }
      }, 500);

    } catch (error) {
      console.error('Error initializing GitHub App:', error);
      toast.error(`Error initializing GitHub App: ${error instanceof Error ? error.message : 'Unknown error'}`);
      finish('error');
    }
  },

  onConnectComplete: async ({ installationId }: { installationId?: number }) => {
    if (!installationId) {
      throw new Error('No installation ID provided');
    }

    return { success: true };
  },

  getFeatures: () => [
    {
      id: 'repositories',
      name: 'Repositories',
      description: 'Connect repositories for security checks',
      enabled: true,
    },
    {
      id: 'issues',
      name: 'GitHub Issues',
      description: 'Track GitHub issues',
      enabled: true,
    },
    {
      id: 'pull_requests',
      name: 'Pull Requests',
      description: 'Track pull requests',
      enabled: true,
    },
    {
      id: 'webhooks',
      name: 'Automatic Updates',
      description: 'Real-time updates via GitHub webhooks',
      enabled: true,
    },
  ],

  syncData: async (integration: IntegrationDTO) => {
    try {
      const installations = await trpcClient.githubApp.listInstallations.query();

      if (!installations || installations.length === 0) {
        console.log('No GitHub App installations found');
        return;
      }

      const connectedRepos = installations.flatMap(installation =>
        installation.repositories.filter(repo => repo.workspaceId === integration.workspaceId)
      );

      if (connectedRepos.length === 0) {
        console.log('No repositories connected to this workspace');
        return;
      }

      const syncPromises = connectedRepos.map(repo =>
        trpcClient.githubApp.syncRepository.mutate({
          repositoryId: repo.id,
        }).catch(error => {
          console.error(`Error syncing repository ${repo.fullName}:`, error);
          return null;
        })
      );

      const results = await Promise.all(syncPromises);
      const successCount = results.filter(r => r !== null).length;

      console.log(`Successfully triggered sync for ${successCount}/${connectedRepos.length} repositories`);

      if (queryClient) {
        queryClient.invalidateQueries({
          queryKey: trpc.githubApp.listSyncJobs.queryKey(),
        });

        queryClient.invalidateQueries({
          queryKey: trpc.sources.list.queryKey(),
        });
      }

      return {
        success: true,
        syncedRepos: successCount,
        totalRepos: connectedRepos.length
      };

    } catch (error) {
      console.error('Error syncing GitHub data:', error);
      throw error;
    }
  },
}

export default GitHubProvider
